import { createSelector } from "reselect";

import * as R from "ramda";

import { getters } from "Portal/ApplicationForm/model";

import { user as getUser } from "redux/modules/user/selectors";

import {
  WELCOME_COLORS,
  LOGIN_COLORS,
  GROUP_COLORS,
  CONTACT_COLORS
} from "Portal/ApplicationForm/constants";

import isValid from "utils/value-types/validations/is-valid";

import isEmpty from "utils/value-types/is-empty";

import Helpers from "utils/Global/Helpers";

import * as STANDARD_MODULE_FIELD_IDS from "@sessionboard/sessionboard-value-types/src/constants/standard-module-field-ids";

export const getIsEditing = createSelector(
  getters.submissionId,
  submissionId => {
    return Boolean(submissionId);
  }
);

export const getWelcomeColor = createSelector(
  getters.activeIndex,
  activeIndex => WELCOME_COLORS[activeIndex]
);

export const getWelcomeData = createSelector(getters.formData, formData =>
  R.isEmpty(formData) ? {} : formData.sections[0]
);

export const getLoginColor = createSelector(
  getters.activeIndex,
  activeIndex => LOGIN_COLORS[activeIndex]
);

export const getGroupColor = createSelector(
  getters.activeIndex,
  activeIndex => GROUP_COLORS[activeIndex]
);

export const getGroupData = createSelector(getters.formData, formData =>
  R.isEmpty(formData) ? {} : formData.sections[2]
);

export const getLogoImageUrl = createSelector(
  getters.formData,
  R.prop("logo_image_url")
);

export const getPageTitle = createSelector(getters.formData, R.prop("title"));

const isRequiredFieldValid = (value, emailId) => {
  if (R.isEmpty(R.dissoc("id", value))) {
    return true;
  }
  const type = R.propOr("text", "type", value);
  if (value.id === emailId) {
    return Helpers.isValidEmail(R.propOr("", "value", value));
  }

  return isValid(value, type);
};

const isFieldValid = (value, emailId) => {
  if (isEmpty(value, value.type)) {
    return true;
  }

  return isRequiredFieldValid(value, emailId);
};

export const getGroupDataFields = createSelector(
  getGroupData,
  getters.groupValues,
  getters.selectedAccountId,
  getters.isClosed,
  (groupData, groupValues, isGroupSelected, isClosed) =>
    R.compose(
      R.map(field => {
        const fieldValue = R.propOr({}, field.id, groupValues);
        return {
          ...field,
          disabled: isClosed
            ? true
            : field.id === STANDARD_MODULE_FIELD_IDS.ACCOUNTS.NAME
            ? Boolean(isGroupSelected)
            : false,
          isValid: !field.required
            ? isFieldValid(
                { ...fieldValue, id: field.id },
                STANDARD_MODULE_FIELD_IDS.ACCOUNTS.EMAIL
              )
            : isRequiredFieldValid(
                { ...fieldValue, id: field.id },
                STANDARD_MODULE_FIELD_IDS.ACCOUNTS.EMAIL
              )
        };
      }),
      R.propOr([], "fields")
    )(groupData)
);

export const getContactColor = createSelector(
  getters.activeIndex,
  activeIndex => CONTACT_COLORS[activeIndex]
);

export const getContactData = createSelector(getters.formData, formData =>
  R.isEmpty(formData) ? {} : formData.sections[1]
);

export const getSelectedSpeakerContactValues = createSelector(
  getters.selectedSpeakerId,
  getters.speakers,
  (selectedSpeakerId, speakers) => {
    return R.compose(
      R.prop("contactValues"),
      R.find(R.propEq("id", selectedSpeakerId))
    )(speakers);
  }
);

export const getContactDataFields = createSelector(
  getContactData,
  getters.contactValues,
  getters.isClosed,
  (contactData, contactValues, isClosed) => {
    return R.compose(
      R.map(field => {
        const fieldValue = R.propOr({}, field.id, contactValues);
        return {
          ...field,
          disabled: isClosed,
          isValid: !field.required
            ? isFieldValid(
                { ...fieldValue, id: field.id },
                STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL
              )
            : isRequiredFieldValid(
                { ...fieldValue, id: field.id },
                STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL,
                contactValues
              )
        };
      }),
      R.propOr([], "fields")
    )(contactData);
  }
);

export const getSpeakerContactDataFields = createSelector(
  getContactData,
  getSelectedSpeakerContactValues,
  getters.selectedContactId,
  getters.isClosed,
  (contactData, contactValues, isContactSelected, isClosed) => {
    return R.compose(
      R.map(field => {
        const fieldValue = R.propOr({}, field.id, contactValues);
        return {
          ...field,
          disabled: isClosed
            ? true
            : [
                (STANDARD_MODULE_FIELD_IDS.CONTACTS.FIRST_NAME,
                STANDARD_MODULE_FIELD_IDS.CONTACTS.LAST_NAME)
              ].includes(field.id)
            ? Boolean(isContactSelected)
            : false,
          isValid: !field.required
            ? isFieldValid(
                { ...fieldValue, id: field.id },
                STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL
              )
            : isRequiredFieldValid(
                { ...fieldValue, id: field.id },
                STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL,
                contactValues
              )
        };
      }),
      R.propOr([], "fields")
    )(contactData);
  }
);

export const getContactDataFieldsFiltered = createSelector(
  getContactDataFields,
  R.filter(
    ({ id }) =>
      ![
        STANDARD_MODULE_FIELD_IDS.CONTACTS.FIRST_NAME,
        STANDARD_MODULE_FIELD_IDS.CONTACTS.LAST_NAME,
        STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL
      ].includes(id)
  )
);

export const getSpeakerContactDataFieldsFiltered = createSelector(
  getSpeakerContactDataFields,
  R.filter(
    ({ id }) =>
      ![
        STANDARD_MODULE_FIELD_IDS.CONTACTS.FIRST_NAME,
        STANDARD_MODULE_FIELD_IDS.CONTACTS.LAST_NAME,
        STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL
      ].includes(id)
  )
);

export const getContactFirstLastNameFields = createSelector(
  getContactDataFields,
  R.compose(
    R.sortBy(({ id }) =>
      R.prop(id)({
        [STANDARD_MODULE_FIELD_IDS.CONTACTS.FIRST_NAME]: 0,
        [STANDARD_MODULE_FIELD_IDS.CONTACTS.LAST_NAME]: 1,
        [STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL]: 2
      })
    ),
    R.filter(({ id }) =>
      [
        STANDARD_MODULE_FIELD_IDS.CONTACTS.FIRST_NAME,
        STANDARD_MODULE_FIELD_IDS.CONTACTS.LAST_NAME,
        STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL
      ].includes(id)
    )
  )
);

export const getSpeakerContactFirstLastNameFields = createSelector(
  getSpeakerContactDataFields,
  R.compose(
    R.sortBy(({ id }) =>
      R.prop(id)({
        [STANDARD_MODULE_FIELD_IDS.CONTACTS.FIRST_NAME]: 0,
        [STANDARD_MODULE_FIELD_IDS.CONTACTS.LAST_NAME]: 1,
        [STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL]: 2
      })
    ),
    R.filter(({ id }) =>
      [
        STANDARD_MODULE_FIELD_IDS.CONTACTS.FIRST_NAME,
        STANDARD_MODULE_FIELD_IDS.CONTACTS.LAST_NAME,
        STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL
      ].includes(id)
    )
  )
);

export const getNextButtonText = createSelector(
  getters.activeIndex,
  activeIndex => {
    if (activeIndex === 0) {
      return "Continue";
    } else if (activeIndex === 1) {
      return "Next Step";
    }
    return "Complete";
  }
);

export const applyRules = (field, fields, values) => {
  const allRulesResults = R.map(rule => {
    const operator = R.prop("operator", rule);
    const conditionField = R.find(
      f => R.prop("form_field_id", f) === R.prop("condition_field_id", rule),
      fields
    );
    const conditionType = R.prop("type", conditionField);

    let actualConditionValue;
    if (conditionType === "dropdown") {
      actualConditionValue = R.pathOr(
        [],
        [conditionField.id, "value", "records"],
        values
      );
    } else {
      actualConditionValue = [R.pathOr([], [conditionField.id], values)];
    }
    const ruleValues = R.pathOr([], ["condition", "values"], rule);

    const ruleValuesResults = R.map(v => {
      const valExists = R.find(av => {
        const condVal = R.prop("value", av) || false;
        const condId = R.prop("id", av);
        const ruleVal =
          conditionType === "checkbox"
            ? JSON.parse(R.prop("value", v))
            : R.prop("value", v);

        return condVal === ruleVal || condId === ruleVal;
      }, actualConditionValue);

      // check if we have transitive conditional logic
      const isExitsTranstiveConditionalField = applyRules(
        conditionField,
        fields,
        values
      );

      // first time  + user select undefined value on the dropdown
      if (
        (valExists === undefined && R.length(actualConditionValue) === 0) ||
        (valExists === undefined && R.isNil(actualConditionValue[0])) ||
        !isExitsTranstiveConditionalField
      ) {
        return false;
      }
      if (operator === "IS" && !valExists) {
        return false;
      } else return !(operator === "IS_NOT" && valExists);
    }, ruleValues);

    return ruleValuesResults.includes(true);
  }, R.propOr([], "rules", field));

  const renderField =
    R.length(allRulesResults) > 0 ? allRulesResults.includes(true) : true;

  return renderField;
};

const getContactValuesValid = createSelector(
  getContactDataFields,
  getters.contactValues,
  (fields, contactValues) => {
    if (R.isEmpty(fields)) {
      return true;
    }

    return R.compose(
      R.all(field => {
        const renderField = applyRules(
          R.find(({ id }) => field.id === id, fields),
          fields,
          contactValues
        );

        if (!renderField) {
          return true;
        }

        if (!field.required) {
          if (R.isEmpty(R.dissoc("id", field)) || isEmpty(field, field.type)) {
            return true;
          }
        }
        const type = R.propOr("text", "type", field);
        if (field.id === STANDARD_MODULE_FIELD_IDS.CONTACTS.EMAIL) {
          return Helpers.isValidEmail(R.propOr("", "value", field));
        }
        return isValid(field, type);
      }),
      R.map(({ id, required }) => ({
        id,
        ...R.propOr({}, id, contactValues),
        required
      }))
    )(fields);
  }
);

export const getLoginValuesValid = createSelector(
  getters.loginValues,
  getters.userExists,
  getters.isPasswordValid,
  getters.termsAccepted,
  (loginValues, userExists, isPasswordValid, termsAccepted) => {
    if (
      R.isEmpty(loginValues.email) ||
      isEmpty(loginValues.email, "email") ||
      !isValid(loginValues.email, "email")
    ) {
      return false;
    }

    if (
      R.isEmpty(loginValues.password) ||
      isEmpty(loginValues.password, "text") ||
      !isValid(loginValues.password, "text")
    ) {
      return false;
    }

    if (!userExists && !isPasswordValid) {
      return false;
    }

    if (!userExists && !termsAccepted) {
      return false;
    }

    return true;
  }
);

export const getLoginData = createSelector(
  getters.loginValues,
  R.map(data => R.propOr("", "value", data))
);

export const getGroupValues = createSelector(
  getters.groupValues,
  R.filter(data => !R.isEmpty(R.propOr("", "value", data)))
);

export const getContactValues = createSelector(
  getters.contactValues,
  R.filter(data => !R.isEmpty(R.propOr("", "value", data)))
);

export const getSpeakerContactValues = createSelector(
  getters.speakers,
  R.map(speaker => ({
    ...speaker,
    contactValues: R.filter(data => !R.isEmpty(R.propOr("", "value", data)))(
      speaker.contactValues
    )
  }))
);

export const getNextButtonDisabled = createSelector(
  getters.activeIndex,
  getContactValuesValid,
  getters.isClosed,
  getters.loading,
  (activeIndex, contactValuesValid, isClosed, isloading) => {
    if (isloading) return true;
    if (activeIndex === 0) {
      return false;
    } else if (activeIndex === 1) {
      return false;
    } else if (activeIndex === 2) {
      return isClosed ? true : !contactValuesValid;
    }
    return false;
  }
);

export const getShowBackButton = createSelector(
  getters.activeIndex,
  activeIndex => activeIndex > 0
);

export const getPortalUrl = createSelector(
  getters.formData,
  data => `/portals/${data.event_slug}/${data.event_uuid}`
);

export const getUserName = createSelector(getUser, user => {
  const name = [user.fname, user.lname].filter(v => v).join(" ");
  if (name.length) {
    return name;
  }
  return user.email;
});

export const getAvailableAccounts = createSelector(
  getters.eventUserGroups,
  accounts => {
    return accounts && accounts.length
      ? [
          ...accounts.map(a => ({
            label: a.name,
            value: a.id
          })),
          {
            value: null,
            label: "Fill out as a new group..."
          }
        ]
      : [];
  }
);

export const getAvailableContacts = createSelector(
  getters.selectedAccountId,
  getters.eventUserGroups,
  (selectedAccountId, accounts) => {
    if (!selectedAccountId) {
      return [];
    }
    const contacts = R.compose(
      R.propOr([], "contacts"),
      R.find(a => a.id === selectedAccountId)
    )(accounts);

    return contacts && contacts.length
      ? [
          ...contacts.map(a => ({
            label: a.name,
            value: a.id
          })),
          {
            value: null,
            label: "Fill out as a new person..."
          }
        ]
      : [];
  }
);

export const getSpeakerTabs = createSelector(
  getters.speakers,
  getters.isClosed,
  (speakers, isClosed) => {
    const tabs = speakers.map((speaker, idx) => {
      const fname = R.path([
        "contactValues",
        STANDARD_MODULE_FIELD_IDS.CONTACTS.FIRST_NAME,
        "value"
      ])(speaker);

      return {
        id: speaker.id,
        title: fname ? fname : `Person ${idx + 1}`
      };
    });

    if (!isClosed && tabs.length < 4) {
      tabs.push({
        id: "add",
        title: "Add +"
      });
    }

    return tabs;
  }
);

export const allowRemovePerson = createSelector(
  getters.speakers,
  getters.isClosed,
  getters.selectedSpeakerId,
  (speakers, isClosed, selectedSpeakerId) => {
    const idx = speakers.findIndex(s => s.id === selectedSpeakerId);

    return isClosed
      ? false
      : idx === 0
      ? false
      : speakers && speakers.length > 1
      ? true
      : false;
  }
);

export const showThisIsYou = createSelector(
  getters.speakers,
  getters.selectedSpeakerId,
  (speakers, selectedSpeakerId) => {
    const idx = speakers.findIndex(s => s.id === selectedSpeakerId);
    return idx === 0 ? true : false;
  }
);
